import { FC, memo, useEffect, ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'
import { useSnapshot } from 'valtio'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@app/constants'

import { Icon, IconColor, IconTypes } from '@app/ui'
import {
  AppMenuState,
  CurrentUserState,
  TipsCreationListState,
} from '@app/storage'
import { useBrandConfig } from '@app/hooks/useBrandConfig'
import TabSelector from '../TabSelector/TabSelector'
import { themeManager } from '@app/helpers/theme'

export const HEADER_HEIGHT = 60

type TitleAlignment = 'left' | 'center' | 'right'

interface Tab {
  id: string
  label: string
  component: ReactNode
}

export interface FilterItem {
  id: string
  label: string
  icon?: ReactNode
}

interface ScreenHeaderWithInboxProps {
  title?: string
  className?: string
  logoImage?: string
  showUserData?: boolean
  onLogoClick?: () => void
  showDropDownIcon?: boolean
  onInboxClicked?: () => void
  leftIconType?: IconTypes
  leftIconColor?: IconColor
  iconSize?: number
  leftIconMenuClicked?: () => void
  hideTitle?: boolean
  showBackButton?: boolean
  onBackClick?: () => void
  titleAlign?: TitleAlignment
  tabs?: Tab[]
  tabsBackgroundColor?: string
  activeTabId?: string
  onTabChange?: (tabId: string) => void
  backgroundImage?: string
  headerBackgroundColor?: string
  backgroundImageHeight?: string
  zIndex?: number
  hideLogo?: boolean
  height?: string
  filters?: FilterItem[]
  activeFilterId?: string
  activeFilterIds?: string[]
  onFilterChange?: (filterId: string) => void
  rightIcon?: ReactNode
  BrandIcon?: ReactNode
  onRightIconClick?: () => void
  showMenuButton?: boolean
  onMenuButtonClick?: () => void
  bottomContent?: ReactNode
  topContainerPaddingTop?: string
  bottomGap?: number
}

const ScreenHeaderWithInbox: FC<ScreenHeaderWithInboxProps> = ({
  headerBackgroundColor='var(--bg-header-solid)',
  className = '',
  title,
  hideTitle = false,
  showBackButton = false,
  onBackClick,
  titleAlign = 'center',
  tabs,
  tabsBackgroundColor,
  activeTabId,
  onTabChange,
  backgroundImage,
  backgroundImageHeight = '80px',
  zIndex = 100,
  hideLogo = false,
  height = '46px',
  filters,
  activeFilterId,
  activeFilterIds = [],
  onFilterChange,
  rightIcon,
  onRightIconClick,
  showMenuButton = false,
  onMenuButtonClick,
  bottomContent,
  topContainerPaddingTop,
  bottomGap = 0,
  BrandIcon,
}) => {
  const { appTitle, logo, showBrandImageInHeader, tipsterInfo } = useBrandConfig()
  const hasCustomBrandIcon = !!BrandIcon
  const defaultBrandIcon = showBrandImageInHeader && tipsterInfo?.imageUrl
    ? <BrandImage src={tipsterInfo.imageUrl} alt={appTitle} />
    : <Icon type={IconTypes.brand} color={themeManager.isLight() ? IconColor.textPrimary : IconColor.white} width={20} height={20} />
  BrandIcon = BrandIcon ?? defaultBrandIcon
  const headerTitle = title ?? (logo ? undefined : appTitle)
  const navigate = useNavigate()
  const { isTouring, tourStage, avatarUrl } = useSnapshot(CurrentUserState)
  const { tipsList } = useSnapshot(TipsCreationListState)
  const { opened: appMenuOpened } = useSnapshot(AppMenuState)


  const filtersScrollRef = useRef<HTMLDivElement>(null)
  const [ isDragging, setIsDragging ] = useState(false)
  const [ startX, setStartX ] = useState(0)
  const [ scrollLeft, setScrollLeft ] = useState(0)
  const [ hasMoved, setHasMoved ] = useState(false)

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!filtersScrollRef.current) return
    setIsDragging(true)
    setHasMoved(false)
    setStartX(e.pageX - filtersScrollRef.current.offsetLeft)
    setScrollLeft(filtersScrollRef.current.scrollLeft)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setTimeout(() => setHasMoved(false), 50)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !filtersScrollRef.current) return
    e.preventDefault()
    const x = e.pageX - filtersScrollRef.current.offsetLeft
    const walk = (x - startX)
    if (Math.abs(walk) > 5) {
      setHasMoved(true)
    }
    filtersScrollRef.current.scrollLeft = scrollLeft - walk
  }

  const handleFilterClickWithDragCheck = (filterId: string) => {
    if (!hasMoved) {
      onFilterChange?.(filterId)
    }
  }

  const isFilterActive = (filterId: string) => {
    if (activeFilterIds.length > 0) {
      return activeFilterIds.includes(filterId)
    }
    if (activeFilterId) {
      return activeFilterId === filterId
    }
    return filterId === 'all'
  }

  useEffect(() => {
    if (isTouring && tourStage.contentTipMenu && (tourStage.ContentDataTip || tourStage.ContentImageTip) && !tipsList.length) {
      AppMenuState.opened = true
    }
  }, [ tourStage, isTouring, tipsList ])

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-offset-total',
      `calc(var(--header-actual-height) + ${bottomGap}px)`,
    )
    return () => {
      document.documentElement.style.removeProperty('--header-offset-total')
    }
  }, [ bottomGap ])

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick()
    } else {
      window.history.length === 1 ? navigate(ROUTES.feed.short) : navigate(-1)
    }
  }

  const handleMenuButtonClick = () => {
    AppMenuState.opened = true
    onMenuButtonClick?.()
  }

  return (
    <>
      <HeaderWrapper data-app-header $hasTabs={!!tabs} $hasBackgroundImage={!!backgroundImage} $hasFilters={!!filters} zIndex={zIndex}>
        <StyledHeader
          className={`${className} `}
          isTouring={isTouring}
          menuOpened={appMenuOpened}
          $backgroundImage={backgroundImage}
          $backgroundImageHeight={backgroundImageHeight}
          $height={height}
          backgroundColor={headerBackgroundColor}
        >
          <TopContentContainer $topContainerPaddingTop={topContainerPaddingTop}>
            {showBackButton && (
              <BackIconButton onClick={handleBackClick}>
                <Icon
                  type={IconTypes.angleLeft}
                  color={backgroundImage || !themeManager.isLight() ? IconColor.white : IconColor.textPrimary}
                  width={23}
                  height={23}
                />
              </BackIconButton>
            )}

            {showMenuButton && (
              <MenuIconButton onClick={handleMenuButtonClick}>
                <Icon
                  type={IconTypes.menu}
                  color={backgroundImage || !themeManager.isLight() ? IconColor.white : IconColor.textPrimary}
                  width={18}
                  height={18}
                />
              </MenuIconButton>
            )}

            {!hideTitle && (
              <LogoContainer
                $hasBackButton={showBackButton}
                $align={titleAlign}
              >
                {hideLogo ? null : logo && !hasCustomBrandIcon ? (
                  <>
                    <BrandLogoImage
                      className="theme-dark-logo"
                      src={logo}
                      alt={appTitle}
                    />
                    <BrandLogoImage
                      className="theme-light-logo"
                      src={logo.replace(/\.svg$/, '-light.svg')}
                      alt={appTitle}
                    />
                  </>
                ) : (
                  BrandIcon
                )}
                {headerTitle}
              </LogoContainer>
            )}

            {rightIcon && (
              <RightIconButton onClick={onRightIconClick}>
                {rightIcon}
              </RightIconButton>
            )}
          </TopContentContainer>

          {bottomContent && (
            <BottomContentContainer>
              {bottomContent}
            </BottomContentContainer>
          )}

        </StyledHeader>


        {filters && filters.length > 0 && (
          <FiltersContainer>
            <FiltersScrollWrapper
              ref={filtersScrollRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              $isDragging={isDragging}
            >
              {filters.map((filter) => (
                <FilterChip
                  key={filter.id}
                  $isActive={isFilterActive(filter.id)}
                  onClick={() => handleFilterClickWithDragCheck(filter.id)}
                  hasIcon={!!filter.icon}
                >
                  {filter.icon &&<IconWrapper>
                    {filter.icon}
                  </IconWrapper>}
                  <TextLabel>{filter.label}</TextLabel>
                </FilterChip>
              ))}
            </FiltersScrollWrapper>
          </FiltersContainer>
        )}

        {tabs && tabs.length > 0 && (
          <TabsContainer>
            <TabSelector
              tabs={tabs}
              defaultTabId={activeTabId}
              onChange={onTabChange}
              contentPadding={0}
              wrapperBackgroundColor={tabsBackgroundColor}
            />
          </TabsContainer>
        )}


      </HeaderWrapper>
    </>

  )
}


const HeaderWrapper = styled.div<{ $hasTabs: boolean; $hasBackgroundImage: boolean; $hasFilters: boolean; zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  z-index: ${props => props.zIndex};
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top, 0px);
    background: var(--bg-header-primary);
    pointer-events: none;
  }

  @media (max-width: 600px) {
    max-width: 100%;
  }
`

const StyledHeader = styled.div<{
  isTouring: boolean
  menuOpened: boolean
  $backgroundImage?: string
  $backgroundImageHeight?: string
  $height?: string
  backgroundColor?: string
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-height: ${props =>
    props.$backgroundImage
      ? `calc(${props.$backgroundImageHeight || '80px'} + env(safe-area-inset-top, 0px))`
      : '106px'};
  height: auto;
  padding: 0 16px;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
   background: ${props => props.$backgroundImage
    ? `linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%), url(${props.$backgroundImage})`
    : `${props.backgroundColor ? props.backgroundColor : 'var(--bg-header-primary)'}`};
  background-size: cover;
  background-position: center;
  overflow: visible;
  position: relative;


`
const TopContentContainer = styled.div<{ $topContainerPaddingTop?: string }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  padding-top: ${props => props.$topContainerPaddingTop};
`
const BottomContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  padding: 24px 0;
  z-index: 10;

    @media (max-width: 480px) {
      padding: 12px 0;

  }
`

const TabsContainer = styled.div`
  padding: 0 12px;
  margin-top: 8px;
  position: relative;
  z-index: 10;
  background: transparent;
`

const BackIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  left: 4px;
  z-index: 10;
  outline: none;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    opacity: 0.7;
    transform: translateX(-2px);
  }

  &:active {
    opacity: 0.5;
    transform: translateX(0);
  }
`

const MenuIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  outline: none;
  padding: 0;
  margin-right: 12px;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }
`

const RightIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  right: 23px;
  z-index: 10;
  outline: none;

  &:hover {
    opacity: 0.7;
    transform: translateX(2px);
  }

  &:active {
    opacity: 0.5;
    transform: translateX(0);
  }
`

const BrandImage = styled.img`
  width: var(--header-brand-image-size, 20px);
  height: var(--header-brand-image-size, 20px);
  object-fit: contain;
`

const BrandLogoImage = styled.img`
  height: 24px;
  object-fit: contain;

  &.theme-light-logo {
    display: none;
  }

  [data-theme="light"] & {
    &.theme-dark-logo {
      display: none;
    }
    &.theme-light-logo {
      display: inline-block;
    }
  }
`

const LogoContainer = styled.div<{
  $hasBackButton?: boolean
  $align: TitleAlignment
}>`
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: var(--header-title-font-size, 11.74px);
  line-height: 15.91px;
  color: #FFFFFF;
  text-transform: uppercase;

  [data-theme="light"] & {
    color: var(--text-primary);
  }
  font-weight: 400;
  font-family: var(--header-title-font-family, 'Michroma', sans-serif);
  z-index: 5;

  ${props => {
    switch (props.$align) {
      case 'left':
        return `
          flex: 1;
          justify-content: flex-start;
          ${props.$hasBackButton ? 'padding-left: 40px;' : ''}
        `
      case 'right':
        return `
          position: absolute;
          right: 16px;
          justify-content: flex-end;
        `
      case 'center':
      default:
        return `
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          justify-content: center;
        `
    }
  }}
`

const FiltersContainer = styled.div`
  padding: 0 16px;
  margin-top: -25.5px;
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    margin-top: -25.5px;
  }
`

const FiltersScrollWrapper = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  align-items: center;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Add scroll padding for better UX */
  scroll-padding: 0 16px;
  -webkit-overflow-scrolling: touch;

  /* Smooth scrolling when not dragging */
  scroll-behavior: ${({ $isDragging }) => ($isDragging ? 'auto' : 'smooth')};
`

const FilterChip = styled.button<{ $isActive: boolean, hasIcon: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  min-height: 51px;
  height: 51px;
  width: ${({ hasIcon }) => (hasIcon ? '78px' : '46px')};
  min-width: ${({ hasIcon }) => (hasIcon ? '78px' : '46px')};
  max-width: ${({ hasIcon }) => (hasIcon ? '78px' : '46px')};
  border: none;
  border-radius: 12px;
  font-size: var(--font-size-normal);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  user-select: none;
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--bg-nonary)' : 'var(--bg-quaternary)'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--r-color-white)' : 'var(--font-color-primary)'};

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  height: 24px;
  flex-shrink: 0;
`

const TextLabel = styled.span`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 11px;
  line-height: 1.1;
  font-weight: 600;
`

export default memo(ScreenHeaderWithInbox)
